import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  IconAlertCircle,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconTerminal,
  IconX,
} from "@tabler/icons-react";
import { AuthContext } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  collectDiagnostics,
  formatDiagnosticSummary,
  type DiagnosticPayload,
} from "@/lib/diagnostics";
import { submitSupportRequest } from "@/lib/supportApi";
import { useToast } from "@/hooks/use-toast";

interface DiagnosticReportModalProps {
  open: boolean;
  onClose: () => void;
  initialCategory?: string;
  initialMessage?: string;
}

interface PagedTextAreaProps {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  ariaLabel: string;
  maxLength?: number;
}

type FormStep = "details" | "message" | "diagnostics" | "review";
type SubmittedStatus = "idle" | "sent" | "queued";

const CATEGORIES = [
  "Feedback & Feature Suggestions",
  "General App Question",
  "Cloud Sync & Backup",
  "E-Paper & Display",
  "Exporting & Publishing",
  "Editor & Planner",
  "Codex & Characters",
  "Account & Novelist Tier",
  "Data Deletion & Privacy Request",
  "Bug / Crash Report",
] as const;

const STEPS: FormStep[] = [
  "details",
  "message",
  "diagnostics",
  "review",
];

const STEP_LABELS: Record<FormStep, string> = {
  details: "Contact Details",
  message: "Your Message",
  diagnostics: "Diagnostic Preview",
  review: "Review and Send",
};

const MAX_MESSAGE_LENGTH = 12000;

function findWhitespaceSplit(text: string, maximum: number): number {
  if (maximum <= 1) return Math.max(1, maximum);
  const minimumUsefulSplit = Math.max(1, Math.floor(maximum * 0.7));

  for (let index = maximum - 1; index >= minimumUsefulSplit; index -= 1) {
    if (/\s/.test(text[index] ?? "")) {
      return index + 1;
    }
  }

  return maximum;
}

function PagedTextArea({
  value,
  onChange,
  readOnly = false,
  placeholder,
  ariaLabel,
  maxLength,
}: PagedTextAreaProps) {
  const visibleRef = useRef<HTMLTextAreaElement>(null);
  const measureRef = useRef<HTMLTextAreaElement>(null);
  const pendingCaretRef = useRef<number | null>(null);
  const [pages, setPages] = useState<string[]>([value]);
  const [pageIndex, setPageIndex] = useState(0);

  const paginate = useCallback((
    completeText: string,
    desiredCaret?: number
  ) => {
    const measure = measureRef.current;
    const visible = visibleRef.current;

    if (!measure || !visible) {
      setPages([completeText]);
      setPageIndex(0);
      return;
    }

    const bounds = visible.getBoundingClientRect();

    if (bounds.width <= 0 || bounds.height <= 0) {
      setPages([completeText]);
      setPageIndex(0);
      return;
    }

    measure.style.width = `${bounds.width}px`;
    measure.style.height = `${bounds.height}px`;

    const fits = (candidate: string): boolean => {
      measure.value = candidate || " ";
      return measure.scrollHeight <= measure.clientHeight + 1;
    };

    const nextPages: string[] = [];
    let remaining = completeText;

    if (remaining.length === 0) {
      nextPages.push("");
    }

    while (remaining.length > 0) {
      if (fits(remaining)) {
        nextPages.push(remaining);
        remaining = "";
        break;
      }

      let lower = 1;
      let upper = remaining.length;
      let best = 1;

      while (lower <= upper) {
        const midpoint = Math.floor((lower + upper) / 2);
        const candidate = remaining.slice(0, midpoint);

        if (fits(candidate)) {
          best = midpoint;
          lower = midpoint + 1;
        } else {
          upper = midpoint - 1;
        }
      }

      const splitAt = findWhitespaceSplit(remaining, best);
      nextPages.push(remaining.slice(0, splitAt));
      remaining = remaining.slice(splitAt);
    }

    let nextPageIndex = Math.min(
      pageIndex,
      Math.max(0, nextPages.length - 1)
    );

    if (typeof desiredCaret === "number") {
      let consumed = 0;

      for (let index = 0; index < nextPages.length; index += 1) {
        const pageEnd = consumed + nextPages[index].length;

        if (desiredCaret <= pageEnd || index === nextPages.length - 1) {
          nextPageIndex = index;
          pendingCaretRef.current = Math.max(
            0,
            desiredCaret - consumed
          );
          break;
        }

        consumed = pageEnd;
      }
    }

    setPages(nextPages);
    setPageIndex(nextPageIndex);
  }, [pageIndex]);

  useLayoutEffect(() => {
    paginate(value);
  }, [value]);

  useEffect(() => {
    const visible = visibleRef.current;
    if (!visible) return;

    const observer = new ResizeObserver(() => paginate(value));
    observer.observe(visible);

    return () => observer.disconnect();
  }, [paginate, value]);

  useLayoutEffect(() => {
    const visible = visibleRef.current;
    const caret = pendingCaretRef.current;

    if (!visible || caret === null || readOnly) return;

    const boundedCaret = Math.min(caret, visible.value.length);
    visible.setSelectionRange(boundedCaret, boundedCaret);
    pendingCaretRef.current = null;
  }, [pageIndex, pages, readOnly]);

  const completeBeforeCurrent = useMemo(
    () => pages.slice(0, pageIndex).join(""),
    [pageIndex, pages]
  );

  const completeAfterCurrent = useMemo(
    () => pages.slice(pageIndex + 1).join(""),
    [pageIndex, pages]
  );

  const currentValue = pages[pageIndex] ?? "";

  const handleChange = (nextPageValue: string) => {
    if (!onChange || readOnly) return;

    const allowedCurrentLength =
      typeof maxLength === "number"
        ? Math.max(
            0,
            maxLength -
              completeBeforeCurrent.length -
              completeAfterCurrent.length
          )
        : Number.POSITIVE_INFINITY;

    const boundedPageValue = nextPageValue.slice(
      0,
      allowedCurrentLength
    );

    const complete =
      completeBeforeCurrent +
      boundedPageValue +
      completeAfterCurrent;

    const absoluteCaret =
      completeBeforeCurrent.length +
      (visibleRef.current?.selectionStart ?? boundedPageValue.length);

    onChange(complete);
    paginate(complete, absoluteCaret);
  };

  const moveToPreviousPage = () => {
    setPageIndex((previous) => Math.max(0, previous - 1));
  };

  const moveToNextPage = () => {
    setPageIndex((previous) =>
      Math.min(pages.length - 1, previous + 1)
    );
  };

  return (
    <div className="h-full min-h-0 flex flex-col gap-2">
      <div className="flex-1 min-h-0 relative">
        <textarea
          ref={visibleRef}
          value={currentValue}
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={(event) => {
            const target = event.currentTarget;

            if (event.key === "PageUp") {
              event.preventDefault();
              moveToPreviousPage();
              return;
            }

            if (event.key === "PageDown") {
              event.preventDefault();
              moveToNextPage();
              return;
            }

            if (
              event.key === "Backspace" &&
              target.selectionStart === 0 &&
              target.selectionEnd === 0 &&
              pageIndex > 0
            ) {
              event.preventDefault();
              setPageIndex(pageIndex - 1);
              pendingCaretRef.current =
                pages[pageIndex - 1]?.length ?? 0;
            }
          }}
          onWheel={(event) => event.preventDefault()}
          readOnly={readOnly}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className="absolute inset-0 w-full h-full resize-none overflow-hidden border-2 border-foreground bg-background text-foreground p-3 font-mono text-sm leading-5 focus:outline-none"
        />

        <textarea
          ref={measureRef}
          tabIndex={-1}
          aria-hidden="true"
          readOnly
          className="fixed -left-[10000px] top-0 resize-none overflow-hidden border-2 border-foreground p-3 font-mono text-sm leading-5 invisible pointer-events-none"
        />
      </div>

      <div className="h-11 shrink-0 flex items-center justify-center gap-4 border border-foreground">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={moveToPreviousPage}
          disabled={pageIndex <= 0}
          aria-label="Previous text page"
        >
          <IconChevronLeft size={20} />
        </Button>

        <span className="min-w-[110px] text-center text-xs font-mono">
          Page {pageIndex + 1} of {Math.max(1, pages.length)}
        </span>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={moveToNextPage}
          disabled={pageIndex >= pages.length - 1}
          aria-label="Next text page"
        >
          <IconChevronRight size={20} />
        </Button>
      </div>
    </div>
  );
}

export function DiagnosticReportModal({
  open,
  onClose,
  initialCategory = "General App Question",
  initialMessage = "",
}: DiagnosticReportModalProps) {
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const { toast } = useToast();

  const [email, setEmail] = useState("");
  const [categoryIndex, setCategoryIndex] = useState(1);
  const [message, setMessage] = useState(initialMessage);
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [diagnostics, setDiagnostics] =
    useState<DiagnosticPayload | null>(null);
  const [step, setStep] = useState<FormStep>("details");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedStatus, setSubmittedStatus] =
    useState<SubmittedStatus>("idle");
  const [isOnline, setIsOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );

  const category = CATEGORIES[categoryIndex];

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user?.email]);

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const matchingCategory = CATEGORIES.findIndex(
      (candidate) => candidate === initialCategory
    );

    setCategoryIndex(matchingCategory >= 0 ? matchingCategory : 1);
    setMessage(initialMessage);
    setStep("details");
    setSubmittedStatus("idle");

    let active = true;

    collectDiagnostics(user?.id)
      .then((payload) => {
        if (active) setDiagnostics(payload);
      })
      .catch(() => {
        if (active) setDiagnostics(null);
      });

    return () => {
      active = false;
    };
  }, [initialCategory, initialMessage, open, user?.id]);

  const stepIndex = STEPS.indexOf(step);
  const diagnosticsText = diagnostics
    ? formatDiagnosticSummary(diagnostics)
    : "Diagnostic information is being collected.";

  const validateCurrentStep = (): boolean => {
    if (step === "details") {
      if (!email || !email.includes("@")) {
        toast({
          title: "Email Required",
          description: "Enter a valid email address so support can reply.",
          variant: "destructive",
        });
        return false;
      }
    }

    if (step === "message" && !message.trim()) {
      toast({
        title: "Message Required",
        description: "Describe the issue or question before continuing.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const goBack = () => {
    setStep(STEPS[Math.max(0, stepIndex - 1)]);
  };

  const goForward = () => {
    if (!validateCurrentStep()) return;
    setStep(STEPS[Math.min(STEPS.length - 1, stepIndex + 1)]);
  };

  const cycleCategory = (direction: -1 | 1) => {
    setCategoryIndex((previous) => {
      const next = previous + direction;

      if (next < 0) return CATEGORIES.length - 1;
      if (next >= CATEGORIES.length) return 0;
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!email || !email.includes("@") || !message.trim()) {
      setStep(!email.includes("@") ? "details" : "message");
      validateCurrentStep();
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitSupportRequest({
        email: email.trim(),
        category,
        message: message.trim(),
        includeDiagnostics,
        userId: user?.id,
      });

      if (result.queuedOffline) {
        setSubmittedStatus("queued");
        toast({
          title: "Saved Offline",
          description:
            "The report will be sent automatically when the device reconnects.",
        });
        return;
      }

      if (!result.success) {
        throw new Error(result.error || "Failed to submit report");
      }

      setSubmittedStatus("sent");
      toast({
        title: "Report Received",
        description: "Birch Support has received your report.",
      });
    } catch (error) {
      toast({
        title: "Submission Error",
        description:
          error instanceof Error
            ? error.message
            : "Error submitting support request",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setStep("details");
    setSubmittedStatus("idle");
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background flex items-center justify-center p-2 md:p-6 pt-safe pb-safe overflow-hidden">
      <div className="w-full max-w-2xl h-full max-h-[760px] min-h-0 bg-background border-2 border-foreground text-foreground flex flex-col overflow-hidden">
        <header className="h-14 shrink-0 border-b-2 border-foreground px-3 md:px-5 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="font-serif text-base md:text-xl font-bold truncate">
              {submittedStatus === "idle"
                ? "Contact Birch Support"
                : "Support Report"}
            </h2>
            {submittedStatus === "idle" && (
              <p className="text-[11px] font-mono">
                Step {stepIndex + 1} of {STEPS.length}: {STEP_LABELS[step]}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={resetAndClose}
            className="w-11 h-11 flex items-center justify-center"
            aria-label="Close support form"
          >
            <IconX size={24} />
          </button>
        </header>

        <main className="flex-1 min-h-0 overflow-hidden p-3 md:p-5">
          {submittedStatus === "sent" && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4">
              <div className="w-14 h-14 border-2 border-foreground rounded-full flex items-center justify-center">
                <IconCheck size={30} />
              </div>
              <h3 className="text-xl font-serif font-bold">
                Report Safely Received
              </h3>
              <p className="max-w-md text-sm">
                Confirmation has been sent to <strong>{email}</strong>.
                Support normally replies within 24 to 48 hours, Monday
                through Friday.
              </p>
              <Button type="button" onClick={resetAndClose}>
                Return to Birch
              </Button>
            </div>
          )}

          {submittedStatus === "queued" && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4">
              <div className="w-14 h-14 border-2 border-foreground rounded-full flex items-center justify-center">
                <IconAlertCircle size={30} />
              </div>
              <h3 className="text-xl font-serif font-bold">
                Queued for Offline Delivery
              </h3>
              <p className="max-w-md text-sm">
                The report is stored on this device and will be transmitted
                automatically when connectivity returns.
              </p>
              <Button type="button" onClick={resetAndClose}>
                Return to Writing
              </Button>
            </div>
          )}

          {submittedStatus === "idle" && step === "details" && (
            <div className="h-full flex flex-col justify-center gap-5 max-w-lg mx-auto">
              <div>
                <label
                  htmlFor="support-email"
                  className="block text-xs font-bold uppercase mb-1"
                >
                  Reply Email
                </label>
                <Input
                  id="support-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="h-12 border-2 border-foreground"
                />
              </div>

              <div>
                <span className="block text-xs font-bold uppercase mb-1">
                  Topic
                </span>
                <div className="h-14 border-2 border-foreground flex items-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => cycleCategory(-1)}
                    aria-label="Previous topic"
                  >
                    <IconChevronLeft size={20} />
                  </Button>

                  <span className="flex-1 text-center text-sm font-bold px-2">
                    {category}
                  </span>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => cycleCategory(1)}
                    aria-label="Next topic"
                  >
                    <IconChevronRight size={20} />
                  </Button>
                </div>
              </div>

              <label className="min-h-[64px] border-2 border-foreground p-3 flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={includeDiagnostics}
                  onChange={(event) =>
                    setIncludeDiagnostics(event.target.checked)
                  }
                  className="w-5 h-5 mt-0.5 accent-foreground"
                />
                <span>
                  <strong className="block text-sm">
                    Include diagnostic data
                  </strong>
                  <span className="block text-xs">
                    Includes device details and sanitized error logs. It
                    never includes manuscript prose.
                  </span>
                </span>
              </label>

              {!isOnline && (
                <div className="border-2 border-foreground p-2 text-xs font-mono text-center">
                  OFFLINE: THE REPORT WILL BE QUEUED
                </div>
              )}
            </div>
          )}

          {submittedStatus === "idle" && step === "message" && (
            <div className="h-full min-h-0 flex flex-col gap-2">
              <div className="shrink-0">
                <h3 className="font-serif font-bold">
                  Describe the issue or feedback
                </h3>
                <p className="text-xs">
                  Page Up and Page Down also turn message pages.
                </p>
              </div>

              <div className="flex-1 min-h-0">
                <PagedTextArea
                  value={message}
                  onChange={setMessage}
                  placeholder="What happened, what did you expect, and what were you doing immediately beforehand?"
                  ariaLabel="Support message"
                  maxLength={MAX_MESSAGE_LENGTH}
                />
              </div>
            </div>
          )}

          {submittedStatus === "idle" && step === "diagnostics" && (
            <div className="h-full min-h-0 flex flex-col gap-2">
              <div className="shrink-0 flex items-center gap-2">
                <IconTerminal size={18} />
                <div>
                  <h3 className="font-serif font-bold">
                    Diagnostic Preview
                  </h3>
                  <p className="text-xs">
                    Manuscript prose is never attached.
                  </p>
                </div>
              </div>

              <div className="flex-1 min-h-0">
                {includeDiagnostics ? (
                  <PagedTextArea
                    value={diagnosticsText}
                    readOnly
                    ariaLabel="Diagnostic preview"
                  />
                ) : (
                  <div className="h-full border-2 border-foreground flex items-center justify-center text-center p-6">
                    <p className="text-sm">
                      Diagnostic attachment is disabled. Only your email,
                      topic, and message will be sent.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {submittedStatus === "idle" && step === "review" && (
            <div className="h-full flex flex-col justify-center gap-4 max-w-lg mx-auto">
              <h3 className="text-xl font-serif font-bold">
                Review Submission
              </h3>

              <dl className="border-2 border-foreground divide-y divide-foreground text-sm">
                <div className="p-3">
                  <dt className="text-xs font-bold uppercase">Email</dt>
                  <dd className="break-all">{email}</dd>
                </div>
                <div className="p-3">
                  <dt className="text-xs font-bold uppercase">Topic</dt>
                  <dd>{category}</dd>
                </div>
                <div className="p-3">
                  <dt className="text-xs font-bold uppercase">Message</dt>
                  <dd>{message.trim().length} characters</dd>
                </div>
                <div className="p-3">
                  <dt className="text-xs font-bold uppercase">
                    Diagnostics
                  </dt>
                  <dd>{includeDiagnostics ? "Included" : "Not included"}</dd>
                </div>
                <div className="p-3">
                  <dt className="text-xs font-bold uppercase">Delivery</dt>
                  <dd>{isOnline ? "Send now" : "Queue offline"}</dd>
                </div>
              </dl>
            </div>
          )}
        </main>

        {submittedStatus === "idle" && (
          <footer className="h-16 shrink-0 border-t-2 border-foreground px-3 md:px-5 flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={stepIndex === 0 ? resetAndClose : goBack}
              className="border-2 border-foreground"
            >
              {stepIndex === 0 ? "Cancel" : "Back"}
            </Button>

            <span className="text-xs font-mono">
              {stepIndex + 1}/{STEPS.length}
            </span>

            {step === "review" ? (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Packaging Report"
                  : isOnline
                    ? "Send Report"
                    : "Queue Report"}
              </Button>
            ) : (
              <Button type="button" onClick={goForward}>
                Continue
              </Button>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}
